import { chatDrawerActions, ChatDrawerMessage, chatDrawerSelectors } from 'ducks/chatDrawer'
import { FeedbackFormOptions, useFeedbackForm } from 'hooks/useFeedbackForm'
import React, { createContext, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'types/state'
import { useChatScroll } from './ChatScrollContext'
import { CHAT_MESSAGE_NEGATIVE_FEEDBACK_OPTIONS } from './constants'

interface ChatMessageFeedbackContextValue {
  state: ReturnType<typeof useFeedbackForm>['state']
  actions: ReturnType<typeof useFeedbackForm>['actions']
  isSubmitting: boolean
  setCustomFeedbackText: (text: string) => void
  isFeedbackSubmitted: boolean
  getFeedbackOptions: () => Array<{ key: string; label: string }>
  getQuestionText: () => string
  handleThumbsClick: (isPositiveValue: boolean) => Promise<void>
  handleChipSubmit: () => Promise<void>
  handleCancel: () => void
}

const ChatMessageFeedbackContext = createContext<ChatMessageFeedbackContextValue | null>(null)

export const useChatMessageFeedback = () => {
  const context = useContext(ChatMessageFeedbackContext)
  if (!context) {
    throw new Error('useChatMessageFeedback must be used within ChatMessageFeedbackProvider')
  }
  return context
}

interface ChatMessageFeedbackProviderProps {
  message: ChatDrawerMessage
  children: React.ReactNode
}

export const ChatMessageFeedbackProvider: React.FC<ChatMessageFeedbackProviderProps> = ({ message, children }) => {
  const dispatch = useDispatch()
  const { scrollToBottom } = useChatScroll()

  // For voice messages without messageId, we don't track submission state
  // For regular messages with messageId, we check if feedback was already submitted
  const isMessageFeedbackSubmitted = useSelector((state: RootState) =>
    message.messageId ? chatDrawerSelectors.isMessageFeedbackSubmitted(state, message.messageId!) : false
  )
  const isFeedbackSubmitted = message.messageId ? isMessageFeedbackSubmitted : false

  const handleSubmit = async (isPositive: boolean, selectedChips: string[], customText: string) => {
    return {
      chat_message_id: message.messageId,
      feedback_metadata: {
        chat_message: {
          content: message.content,
          direction: message.direction,
          origin: message.origin,
          message_timestamp: new Date().toISOString(),
          message_length: message.content.length,
          has_button_actions: message.buttonActions?.length > 0,
          is_voice_message: message.origin === 'voice',
          has_message_id: !!message.messageId,
          message_type: message.origin === 'voice' ? 'voice_message' : 'text_message',
        },
      },
    }
  }

  const feedbackOptions: FeedbackFormOptions = {
    featureType: 'ada_chat',
    thumbsUpBehavior: 'submit',
    thumbsDownBehavior: 'submitSilentlyAndShowChips',
    customSubmitHandler: handleSubmit,
    onShowChips: () => {
      setTimeout(() => scrollToBottom(), 100)
    },
    onSuccess: (skipIsSubmitted) => {
      // Only mark as submitted if the message has a messageId
      if (!skipIsSubmitted && message.messageId) {
        dispatch(chatDrawerActions.markMessageFeedbackSubmitted(message.messageId))
      }
    },
    onError: (error) => {
      console.error('Chat feedback submission error:', error)
    },
  }

  const { state, actions, isSubmitting, setCustomFeedbackText } = useFeedbackForm(feedbackOptions)

  const handleThumbsClick = async (isPositiveValue: boolean) => {
    await actions.handleThumbsClick(isPositiveValue)
  }

  const handleChipSubmit = async () => {
    await actions.handleChipSubmit()
  }

  const handleCancel = () => {
    actions.handleCancel()
  }

  const getFeedbackOptions = () => {
    const options = state.isPositive ? [] : CHAT_MESSAGE_NEGATIVE_FEEDBACK_OPTIONS
    return options.map((option) => ({ key: option, label: option }))
  }

  const getQuestionText = () => {
    // This will be translated in the component that uses it
    if (state.isPositive) {
      return 'What did you like? Select all that apply:'
    }
    return 'What went wrong? Select all that apply:'
  }

  const value: ChatMessageFeedbackContextValue = {
    state,
    actions,
    isSubmitting,
    setCustomFeedbackText,
    isFeedbackSubmitted,
    getFeedbackOptions,
    getQuestionText,
    handleThumbsClick,
    handleChipSubmit,
    handleCancel,
  }

  return <ChatMessageFeedbackContext.Provider value={value}>{children}</ChatMessageFeedbackContext.Provider>
}
